import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, FileText, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge, Empty, SelectField } from "@/components/common";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { BILLING_STATUS, fmtMin, fmtPrice, todayStr, fmtDate } from "@/lib/constants";

const pad = (n) => String(n).padStart(2, "0");
const lastDay = (y, m) => new Date(y, m, 0).getDate();
const rangeOf = (mode, month, year, from, to) => {
  if (mode === "month") {
    if (!/^\d{4}-\d{2}$/.test(month || "")) return ["", ""];
    const [y, m] = month.split("-").map(Number);
    return [`${month}-01`, `${month}-${pad(lastDay(y, m))}`];
  }
  if (mode === "year") return [`${year}-01-01`, `${year}-12-31`];
  return [from, to];
};
const hm = (iso) => new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" });

const Sessions = ({ id }) => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/interventions/${id}/time`).then((r) => setS(r.data.sessions)); }, [id]);
  if (!s) return <p className="text-xs text-slate-500">Chargement…</p>;
  if (!s.length) return <p className="text-xs text-slate-500">Aucune session chronométrée.</p>;
  return (
    <ul className="space-y-0.5 text-xs text-slate-400" data-testid={`summary-sessions-${id}`}>
      {s.map((x) => <li key={x.id}>{hm(x.start)} → {x.end ? hm(x.end) : "en cours"} · {fmtMin(Math.round(x.seconds / 60))}{x.manual ? " · saisie manuelle" : ""}{x.user_name ? ` · ${x.user_name}` : ""}</li>)}
    </ul>
  );
};

const InterRow = ({ r }) => {
  const [open, setOpen] = useState(false);
  const c = r.computed;
  return (
    <li className="rounded-lg border border-white/5 p-2.5">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <button onClick={() => setOpen(!open)} className="text-slate-400" data-testid={`summary-inter-toggle-${r.id}`}>{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button>
        <Link to={`/interventions/${r.id}`} className="min-w-0 flex-1 truncate text-white hover:text-cyan-300">{fmtDate(r.date)} · {r.title}</Link>
        <span className="font-mono-dt text-xs text-slate-400">{fmtMin(c.tracked_minutes)} / {fmtMin(c.retained_minutes)} / {fmtMin(c.billable_minutes)}</span>
        {c.billing_status && <Badge value={c.billing_status} map={BILLING_STATUS} />}
        <span className="w-24 text-right text-white">{fmtPrice(c.to_bill)}</span>
      </div>
      {open && <div className="mt-2 pl-6"><Sessions id={r.id} /></div>}
    </li>
  );
};

const ClientBlock = ({ g }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-3" data-testid={`summary-client-${g.client_id || "none"}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center gap-3 text-left" data-testid={`summary-client-toggle-${g.client_id || "none"}`}>
        {open ? <ChevronDown className="h-4 w-4 text-cyan-300" /> : <ChevronRight className="h-4 w-4 text-cyan-300" />}
        <span className="min-w-0 flex-1 basis-40 font-medium text-white">{g.client_name} <span className="text-xs text-slate-400">· {g.count} intervention(s)</span></span>
        <span className="font-mono-dt text-xs text-slate-300">{fmtMin(g.tracked_minutes)} / {fmtMin(g.retained_minutes)} / {fmtMin(g.billable_minutes)}</span>
        <span className="text-right"><span className="block font-semibold text-white">{fmtPrice(g.to_bill)}</span><span className="text-xs text-amber-300">reste {fmtPrice(g.remaining)}</span></span>
      </button>
      {open && <ul className="mt-3 space-y-1.5">{g.interventions.map((r) => <InterRow key={r.id} r={r} />)}</ul>}
    </div>
  );
};

export const BillingSummary = () => {
  const now = todayStr();
  const [p, setP] = useState({ mode: "month", month: now.slice(0, 7), year: now.slice(0, 4), from: `${now.slice(0, 7)}-01`, to: now });
  const [data, setData] = useState(null);
  const [from, to] = rangeOf(p.mode, p.month, p.year, p.from, p.to);
  const load = useCallback(() => {
    if (!from || !to) return;
    api.get("/billing/summary", { params: { date_from: from, date_to: to } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);
  const exp = (format) => {
    if (!from || !to) { toast.error("Choisissez une période valide"); return; }
    downloadFile("/billing/export", { scope: "all", format, date_from: from, date_to: to }, format === "csv" ? `dtech42-recapitulatif-${from}-${to}.csv` : undefined)
      .catch((e) => toast.error(formatApiError(e)));
  };
  const t = data?.totals || {};
  return (
    <div className="space-y-4" data-testid="billing-summary">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <SelectField value={p.mode} onChange={(v) => setP({ ...p, mode: v })} options={{ month: "Mois", year: "Année", custom: "Dates personnalisées" }} testid="summary-mode-select" />
        {p.mode === "month" && <Input type="month" value={p.month} onChange={(e) => setP({ ...p, month: e.target.value })} data-testid="summary-month-input" />}
        {p.mode === "year" && <Input type="number" min={2000} max={2100} value={p.year} onChange={(e) => setP({ ...p, year: e.target.value })} data-testid="summary-year-input" />}
        {p.mode === "custom" && <>
          <Input type="date" value={p.from} onChange={(e) => setP({ ...p, from: e.target.value })} data-testid="summary-from-input" />
          <Input type="date" value={p.to} onChange={(e) => setP({ ...p, to: e.target.value })} data-testid="summary-to-input" />
        </>}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {[["Interventions", t.count ?? "—", "count"], ["Chronométré", fmtMin(t.tracked_minutes), "tracked"], ["Retenu", fmtMin(t.retained_minutes), "retained"],
          ["Facturable", fmtMin(t.billable_minutes), "billable"], ["Montant estimatif", fmtPrice(t.to_bill), "to-bill"], ["Reste à facturer", fmtPrice(t.remaining), "remaining"]].map(([k, v, id]) => (
          <div key={k} className="rounded-xl border border-cyan-400/15 bg-[#122549]/70 p-3">
            <p className="text-xs uppercase tracking-wider text-slate-400">{k}</p>
            <p className="mt-1 font-display text-xl font-semibold text-cyan-300" data-testid={`summary-total-${id}`}>{v}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex-1 text-xs text-slate-500">Durées : chronométré / retenu / facturable. Chaque intervention est comptée une seule fois, à la date de son début réel.{data?.vat_mention ? ` ${data.vat_mention}.` : ""}</p>
        <Button variant="outline" className="h-11" onClick={() => exp("csv")} data-testid="summary-export-csv-button"><FileSpreadsheet className="mr-2 h-4 w-4" />CSV</Button>
        <Button variant="outline" className="h-11" onClick={() => exp("pdf")} data-testid="summary-export-pdf-button"><FileText className="mr-2 h-4 w-4" />PDF</Button>
      </div>
      {data && !data.clients.length && <Empty testid="summary-empty">Aucune intervention réalisée sur cette période.</Empty>}
      <div className="space-y-2">{(data?.clients || []).map((g) => <ClientBlock key={g.client_id || "none"} g={g} />)}</div>
    </div>
  );
};
